/**
 * InkyPic - Main Application Component
 * "Bring Your Memories to Life"
 */

import React, { useState } from 'react';
import './styles/global.css';
import './App.css';

// Components
import Header from './components/Header';
import ImageUploader from './components/ImageUploader';
import LoadingSpinner from './components/LoadingSpinner';
import ImagePreview from './components/ImagePreview';

// Services
import { colorizeImage, validateImageFile, getDownloadUrl } from './services/api';

function App() {
  const [originalImage, setOriginalImage] = useState(null);
  const [originalImagePreview, setOriginalImagePreview] = useState(null);
  const [colorizedImage, setColorizedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Handle image selection
  const handleImageSelect = async (file) => {
    // Reset previous state
    setError(null);
    setColorizedImage(null);

    // Validate file
    const validation = validateImageFile(file);
    if (!validation.success) {
      setError(validation.error);
      return;
    }

    // Set original image
    setOriginalImage(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setOriginalImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);

    // Start colorization
    await handleColorize(file);
  };

  // Handle colorization process
  const handleColorize = async (file) => {
    setLoading(true);
    setError(null);

    try {
      console.log('[InkyPic] Starting colorization...');

      // Call API
      const response = await colorizeImage(file);

      if (response.success) {
        console.log('[InkyPic] Colorization successful!');

        // Set colorized image URL
        const colorizedUrl = getDownloadUrl(response.data.colorized_filename);
        setColorizedImage(colorizedUrl);
      } else {
        throw new Error(response.message || 'Colorization failed');
      }
    } catch (err) {
      console.error('[InkyPic] Error:', err);
      setError(err.message || 'Failed to colorize image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle reset
  const handleReset = () => {
    setOriginalImage(null);
    setOriginalImagePreview(null);
    setColorizedImage(null);
    setError(null);
  };

  // Handle download
  const handleDownload = () => {
    if (colorizedImage) {
      const link = document.createElement('a');
      link.href = colorizedImage;
      link.download = `inkypic_colorized_${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="App">
      <Header />

      <main className="main-content">
        <div className="container">
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {!colorizedImage && !loading && (
            <div className="upload-section">
              <ImageUploader
                onImageSelect={handleImageSelect}
                disabled={loading}
              />
            </div>
          )}

          {loading && <LoadingSpinner message="Colorizing your image..." />}

          {colorizedImage && !loading && (
            <ImagePreview
              originalImage={originalImagePreview}
              colorizedImageUrl={colorizedImage}
              onReset={handleReset}
              onDownload={handleDownload}
            />
          )}
        </div>
      </main>

      <footer className="footer">
        <p>Designed and Developed by Bhargava Sharabha</p>
      </footer>
    </div>
  );
}

export default App;
